import { z } from "zod";

// ---------- Habit types ----------

export const HabitType = z.enum(["boolean", "counter", "timer"]);
export type HabitType = z.infer<typeof HabitType>;

export const HabitGroup = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(64),
  icon: z.string().min(1).max(8),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  sortOrder: z.number().int(),
  createdAt: z.string().datetime(),
});
export type HabitGroup = z.infer<typeof HabitGroup>;

export const Habit = z.object({
  id: z.string().uuid(),
  groupId: z.string().uuid(),
  name: z.string().min(1).max(120),
  type: HabitType,
  icon: z.string().min(1).max(8),
  targetValue: z.number().int().nonnegative(),
  xpValue: z.number().int().nonnegative(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: z.string().datetime(),
  isOneOff: z.boolean().default(false),
  targetDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  timeOfDay: z.string().default("morning"),
});
export type Habit = z.infer<typeof Habit>;

export const HabitWithStreak = Habit.extend({
  currentStreak: z.number().int().nonnegative(),
  longestStreak: z.number().int().nonnegative(),
  lastCompleted: z.string().nullable(),
  freezeUsedToday: z.boolean(),
  groupName: z.string(),
  groupIcon: z.string(),
  groupColor: z.string(),
});
export type HabitWithStreak = z.infer<typeof HabitWithStreak>;

export const CreateHabitGroupInput = z.object({
  name: z.string().min(1).max(64),
  icon: z.string().min(1).max(8).default("◦"),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).default("#f59e0b"),
});

export const CreateHabitInput = z.object({
  groupId: z.string().uuid(),
  name: z.string().min(1).max(120),
  type: HabitType,
  icon: z.string().min(1).max(8).default("·"),
  targetValue: z.number().int().nonnegative().default(1),
  xpValue: z.number().int().nonnegative().default(10),
  isOneOff: z.boolean().default(false),
  targetDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  timeOfDay: z.string().default("morning"),
});

export const ReorderInput = z.object({
  orderedIds: z.array(z.string().uuid()),
});

// ---------- Completions ----------

export const CompletionValue = z.object({
  value: z.number().int().nonnegative(),
  notes: z.string().max(2000).optional(),
});
export type CompletionValue = z.infer<typeof CompletionValue>;

export const Completion = z.object({
  id: z.string().uuid(),
  habitId: z.string().uuid(),
  date: z.string(), // YYYY-MM-DD
  value: z.number().int(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type Completion = z.infer<typeof Completion>;

// ---------- Streak ----------

export const Streak = z.object({
  currentStreak: z.number().int().nonnegative(),
  longestStreak: z.number().int().nonnegative(),
  lastCompleted: z.string().nullable(),
  freezeUsedToday: z.boolean(),
});
export type Streak = z.infer<typeof Streak>;

// ---------- Stats ----------

export const HeatmapCell = z.object({
  date: z.string(),
  count: z.number().int().nonnegative(),
});
export type HeatmapCell = z.infer<typeof HeatmapCell>;

export const HabitTrend = z.object({
  habitId: z.string().uuid(),
  points: z.array(z.object({
    date: z.string(),
    value: z.number().int(),
  })),
});
export type HabitTrend = z.infer<typeof HabitTrend>;

// ---------- Journal ----------

export const JournalEntry = z.object({
  id: z.string().uuid(),
  date: z.string(),
  title: z.string().nullable().optional(),
  content: z.string(),
  aiSummary: z.string().nullable(),
  moodScore: z.number().int().min(1).max(5).nullable(),
  createdAt: z.string().datetime(),
});
export type JournalEntry = z.infer<typeof JournalEntry>;

export const QuickThought = z.object({
  id: z.string().uuid(),
  content: z.string().min(1).max(1000),
  deferTo: z.enum(["today", "tomorrow", "evening", "weekly"]).nullable(),
  isProcessed: z.boolean(),
  createdAt: z.string().datetime(),
});
export type QuickThought = z.infer<typeof QuickThought>;

// ---------- LLM Providers ----------

export const ProviderType = z.enum([
  "anthropic",
  "openai",
  "openrouter",
  "groq",
  "ollama",
]);
export type ProviderType = z.infer<typeof ProviderType>;

export const LlmProvider = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(80),
  baseUrl: z.string().url(),
  modelId: z.string().min(1).max(200),
  providerType: ProviderType,
  isActive: z.boolean(),
  createdAt: z.string().datetime(),
  hasApiKey: z.boolean(),
});
export type LlmProvider = z.infer<typeof LlmProvider>;

export const CreateLlmProviderInput = z.object({
  name: z.string().min(1).max(80),
  baseUrl: z.string().url(),
  apiKey: z.string().min(1),
  modelId: z.string().min(1).max(200),
  providerType: ProviderType,
  isActive: z.boolean().default(false),
});

// ---------- AI Sessions ----------

export const SessionType = z.enum([
  "morning",
  "evening",
  "weekly",
  "monthly",
  "ondemand",
]);
export type SessionType = z.infer<typeof SessionType>;

export const ChatMessage = z.object({
  role: z.enum(["user", "assistant", "system", "tool"]),
  content: z.string(),
});
export type ChatMessage = z.infer<typeof ChatMessage>;

export const ChatRequest = z.object({
  sessionType: SessionType,
  message: z.string().min(1).max(4000),
  conversationId: z.string().uuid().optional(),
});
export type ChatRequest = z.infer<typeof ChatRequest>;

// ---------- Daily Plans ----------

export const DailyPlan = z.object({
  id: z.string().uuid(),
  date: z.string(),
  habitsJson: z.array(z.string()),
  notes: z.string().nullable(),
  aiGenerated: z.boolean(),
  autoGenerated: z.boolean(),
  createdAt: z.string().datetime(),
});
export type DailyPlan = z.infer<typeof DailyPlan>;

// ---------- User Stats ----------

export const UserStats = z.object({
  totalXp: z.number().int().nonnegative(),
  level: z.number().int().nonnegative(),
  currentThemeAccent: z.string(),
  timezone: z.string(),
  createdAt: z.string().datetime(),
  daysTracked: z.number().int().nonnegative(),
  daysRemainingInYear: z.number().int().nonnegative(),
  username: z.string().optional(),
  customQuote: z.string().optional(),
  avatarAscii: z.string().optional(),
  customTitle: z.string().optional(),
});
export type UserStats = z.infer<typeof UserStats>;

// ---------- Calendar ----------

export const CalendarEvent = z.object({
  id: z.string(),
  summary: z.string(),
  start: z.string(),
  end: z.string(),
  allDay: z.boolean(),
});
export type CalendarEvent = z.infer<typeof CalendarEvent>;

// ---------- Generic API response ----------

export const ApiError = z.object({
  error: z.string(),
  details: z.unknown().optional(),
});
export type ApiError = z.infer<typeof ApiError>;
